import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import Icon from '../../base/icons/components/Icon';
import { IconPin } from '../../base/icons/svg';
import { getParticipantDisplayName } from '../../base/participants/functions';
import SecondScreenTile from './SecondScreenTile';
/**
 * The filmstrip tile width and height in pixels (16:9).
 */
const TILE_WIDTH = 160;
const TILE_HEIGHT = 90;
/**
 * The styles, injected into the second window via its own Emotion cache.
 */
const useStyles = makeStyles()(() => {
    return {
        item: {
            position: 'relative',
            flexShrink: 0,
            padding: 0,
            background: 'none',
            border: '2px solid transparent',
            borderRadius: 6,
            cursor: 'pointer',
            lineHeight: 0,
            transition: 'border-color 0.2s ease',
            '&:hover': {
                borderColor: 'rgba(255, 255, 255, 0.4)'
            }
        },
        pin: {
            position: 'absolute',
            top: 6,
            right: 6,
            zIndex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 4,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            borderRadius: 4,
            color: '#fff',
            lineHeight: 0,
            pointerEvents: 'none'
        }
    };
});
/**
 * A clickable filmstrip tile in the second-screen stage layout: the shared {@link
 * SecondScreenTile} wrapped in a button that pins the participant to the stage by
 * dispatching {@code setSecondScreen} through the external-API control plane
 * (handled by the parent's {@code onSelect}). A pin badge marks the pinned tile;
 * clicking it again unpins. Memoized so a dominant-speaker or pin change only
 * re-renders the affected tiles.
 *
 * @param {IProps} props - The component props.
 * @returns {ReactElement}
 */
const SecondScreenStageTile = ({ isActiveSpeaker, onSelect, participantId, pinned, win }) => {
    const { classes } = useStyles();
    const { t } = useTranslation();
    const name = useSelector((state) => getParticipantDisplayName(state, participantId));
    const _onClick = useCallback(() => {
        onSelect(participantId);
    }, [onSelect, participantId]);
    return (React.createElement("button", { "aria-label": t(pinned ? 'multiScreen.unpinFromStage' : 'multiScreen.showOnStage', { name }), "aria-pressed": pinned, className: classes.item, onClick: _onClick, type: 'button' },
        React.createElement(SecondScreenTile, { height: TILE_HEIGHT, isActiveSpeaker: isActiveSpeaker, participantId: participantId, width: TILE_WIDTH, win: win }),
        pinned && (React.createElement("span", { className: classes.pin },
            React.createElement(Icon, { color: '#fff', size: 14, src: IconPin })))));
};
export default React.memo(SecondScreenStageTile);
